import { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, MeshDistortMaterial, Sparkles } from "@react-three/drei";

const Shape = ({ position, scale = 1, delay = 0, speed = 1, children }) => {
  const ref = useRef();
  const born = useRef(null);
  useFrame((state) => {
    if (born.current === null) born.current = state.clock.elapsedTime + delay;
    const t = Math.min(1, Math.max(0, (state.clock.elapsedTime - born.current) / 1.4));
    const e = 1 - Math.pow(1 - t, 4);
    ref.current.scale.setScalar(e * scale || 0.001);
  });
  return (
    <Float speed={1.2 * speed} rotationIntensity={0.9} floatIntensity={1.4}>
      <group ref={ref} position={position} scale={0.001}>
        {children}
      </group>
    </Float>
  );
};

const Rig = ({ children }) => {
  const ref = useRef();
  useFrame((state, delta) => {
    const k = Math.min(1, delta * 2.5);
    ref.current.rotation.y += (state.pointer.x * 0.28 - ref.current.rotation.y) * k;
    ref.current.rotation.x += (-state.pointer.y * 0.16 - ref.current.rotation.x) * k;
  });
  return <group ref={ref}>{children}</group>;
};

export const HeroScene = () => (
  <Canvas
    dpr={[1, 1.75]}
    camera={{ position: [0, 0, 9], fov: 45 }}
    gl={{ antialias: true, alpha: true }}
    style={{ position: "absolute", inset: 0 }}
  >
    <ambientLight intensity={1.1} />
    <directionalLight position={[5, 6, 5]} intensity={1.6} />
    <pointLight position={[-6, -4, 2]} intensity={24} color="#FF7A59" />
    <pointLight position={[6, 3, -2]} intensity={18} color="#FFC857" />
    <Rig>
      <Shape position={[3.6, 1.4, -1]} scale={1.7} delay={0.1} speed={1.2}>
        <mesh>
          <sphereGeometry args={[1.15, 48, 48]} />
          <MeshDistortMaterial color="#8CC63F" distort={0.42} speed={1.6} roughness={0.28} />
        </mesh>
      </Shape>
      <Shape position={[-4.1, 0.6, -2]} scale={1.25} delay={0.35} speed={1}>
        <mesh rotation={[0.6, 0.4, 0]}>
          <torusGeometry args={[1, 0.42, 24, 64]} />
          <meshStandardMaterial color="#FF7A59" roughness={0.22} />
        </mesh>
      </Shape>
      <Shape position={[-2.6, 2.3, -3]} scale={0.62} delay={0.55} speed={1.6}>
        <mesh>
          <icosahedronGeometry args={[1, 0]} />
          <meshStandardMaterial color="#4EA8DE" roughness={0.25} flatShading />
        </mesh>
      </Shape>
      <Shape position={[2.2, -2.1, -1.5]} scale={0.8} delay={0.7} speed={1.4}>
        <mesh rotation={[0.3, 0.5, 0.2]}>
          <torusKnotGeometry args={[0.55, 0.2, 96, 16]} />
          <meshStandardMaterial color="#E07A5F" roughness={0.3} />
        </mesh>
      </Shape>
      <Shape position={[4.6, -1.6, -3]} scale={0.55} delay={0.85} speed={1.8}>
        <mesh>
          <sphereGeometry args={[1, 32, 32]} />
          <meshStandardMaterial color="#FFC857" roughness={0.2} />
        </mesh>
      </Shape>
      <Shape position={[-4.6, -2.2, -2.5]} scale={0.5} delay={1} speed={1.5}>
        <mesh rotation={[0.8, 0.2, 0.4]}>
          <capsuleGeometry args={[0.5, 0.9, 8, 24]} />
          <meshStandardMaterial color="#FFC857" roughness={0.25} />
        </mesh>
      </Shape>
      <Sparkles count={70} scale={[13, 7, 5]} size={2.4} speed={0.35} color="#FFC857" opacity={0.55} />
    </Rig>
  </Canvas>
);
